import Contact from '@models/Contact';
import { Set } from 'immutable';
import type { ID, ReduxAction } from '../../types';

type ViewPathname = string;

interface SavedContactSelection {
  pathname?: ViewPathname;
  contact?: Contact;
  contactBatch?: Set<ID> | null;
}

// We might consider moving the selected note (note currently being edited) into
// this slice of state. The "selected" contact is the currently highlighted contact.
export interface SelectedState {
  contact: Contact | null;
  contactEventSource: string | null;
  contactBatch: Set<ID>;
  contactBatchEventSource: string | null;
  savedContactSelection: SavedContactSelection | null;
}

const initialSelectedState = {
  contact: null,
  contactEventSource: null,
  contactBatch: Set<ID>(),
  contactBatchEventSource: null,
  savedContactSelection: null,
};

const pathEffectsSelectionState = (pathname: string): boolean => {
  if (!pathname) {
    return false;
  }
  return !(pathname.startsWith('/contact') || pathname == '/settings');
};

const contactIDForPath = (pathname: string): ID | null => {
  const match = pathname.match(/\/contact\/(\d+)/);
  return match ? match[1] : null;
};

export function selectedReducer(
  state: SelectedState = { ...initialSelectedState },
  { type, payload }: ReduxAction,
) {
  switch (type) {
    // a contact can be selected either by viewing that contact's profile or by
    // highlighting the row of that contact in search/inbox/reminders/group detail view
    case 'SELECT_CONTACT':
      return {
        ...state,
        contact: payload.contact,
        contactEventSource: payload.contactEventSource,
      };

    case 'SELECT_CONTACT_BATCH':
      return {
        ...state,
        contactBatch: payload.contactBatch || Set<ID>(),
        contactBatchEventSource: payload.contactBatchEventSource,
      };

    /*
      We want to maintain contact/contactBatch selection state across navigation
      events. Routes that don't effect selection state (DOES NOT) are contact profiles and
      /settings... all other routes impact selection state in some way (DOES). We use the
      following conditions:
      1. Review, Notes, and Reminders have a separate mechanism for Contact
          selection, because the highlightable data in those views has selectable
          Contact data nested inside of it. When navigating to these views, we clear
          out any contactBatch and saved selection state and let those views manually
          set the selected contact.
      2. Navigating to a destination where the last saved selection state has a pathname
          that matches the destination's. Restore state from the saved selection state.
      3. Navigating DOES -> DOES NOT.
          Save the current selection state and clear current values. (e.g. search -> contact profile)
      4. Navigating DOES NOT -> DOES NOT.
          Do nothing. (e.g. contact profile -> contact profile)
      5. Navigating DOES -> DOES.
          Blank out the current and saved states. (e.g. search -> people)
      6. All other cases, which would be a DOES NOT -> DOES that was not matched by
          condition 2: blank out the current and saved states.
    */
    case 'UPDATE_SELECTED_STATE_ON_NAVIGATE':
      const { to, from } = payload;
      const toPathEffectsSelectionState = pathEffectsSelectionState(to);
      const fromPathEffectsSelectionState = pathEffectsSelectionState(from);
      const contactID = contactIDForPath(to);

      if (!!contactID && contactID === state.contact?.id) {
        return state;
      } else if (!!contactID) {
        return {
          ...state,
          contact: { id: contactID } as Contact,
          contactBatch: Set<ID>(),
        };
      }

      // (1)
      if (['/review', '/notes', '/reminders'].includes(to)) {
        return {
          ...state,
          contactBatch: Set<ID>(),
          savedContactSelection: null,
        };
        // (2)
      } else if (state.savedContactSelection?.pathname == to) {
        return {
          ...state,
          contact: state.savedContactSelection.contact,
          contactBatch: state.savedContactSelection.contactBatch,
          savedContactSelection: null,
        };
        // (3)
      } else if (fromPathEffectsSelectionState && !toPathEffectsSelectionState) {
        return {
          ...state,
          contact: null,
          contactBatch: Set<ID>(),
          savedContactSelection: {
            pathname: from,
            contact: state.contact,
            contactBatch: state.contactBatch,
          },
        };
        // (4)
      } else if (!fromPathEffectsSelectionState && !toPathEffectsSelectionState) {
        return state;
        // (5, 6)
      } else {
        return { ...initialSelectedState };
      }

    default:
      return state;
  }
}
